(function () {
    "use strict";

    /**
     * Proportional Inventory Management (title-first, SKU-keyed)
     *
     * This script chooses a subset of SKUs (weighted + campaign-gated), persists that choice,
     * then applies the result to DOM nodes inside an explicit container.
     *
     * Markup contract (scalable / opt-in):
     * - Container: add `data-inventory-mgmt-container`
     * - Managed items: add `data-inventory-mgmt-item` ONLY on the elements that should be hidden/shown/reordered
     * - Required on each managed item (for title-first matching):
     *   - `data-variant-title="..."`  (must match `WEB_SKUS[sku].variantTitle`, case/whitespace-insensitive)
     * - Optional on each managed item (fallback / debugging):
     *   - `data-variant-sku="..."`    (used only as a fallback locator; internal logic remains SKU-keyed)
     *
     * Optional behavior:
     * - Reorder: set `data-inventory-mgmt-reorder="true"` on the container to reorder visible items.
     *   Reordering happens within the matched item’s parent element (e.g. keeps <li> inside its <ul>).
     *
     * Safety:
     * - If the container has 0 managed items, the script does nothing (logs a debug message if enabled).
     * - If managed items exist but are not a 100% match to `WEB_SKUS` mapping, the script bails out.
     */

    const DESIRED_VISIBLE = 10;
    const STORAGE_KEY = "carpe_visible_skus_20261027";
    // ── Proportional weights (0–100) ─────────────────────────────
    // 100 = guaranteed every visit, 0 = never shown (unless campaign-gated).
    // Edit weights here without touching the SKU catalog below.
    var SKU_WEIGHTS = {
        'CP-UA-FP': 100,   // Fresh Powder
        'CP-UA-LC': 100,     // Lavender Citrus
        'CP-UA-SW': 100,    // Sandalwood
        'CP-UA-MB': 100,    // Mountain Breeze
        'CP-UA-CV': 100,    // Coconut Vanilla
        'CP-UA-CL': 100,   // Clean Sport
        'CP-UA-BO': 0,    // Bergamot & Oud
        'CP-UA-SP': 0,   // Cucumber Spa
        'CP-UA-FF': 100,     // Fragrance Free
        'CP-UA-RW': 100,     // Rose Water
        'CP-UA': 0,     // Eucalyptus
        'CP-UA-OS': 100,     // Ocean Storm
        'CP-UA-BP': 0,     // Bourbon Pepper
        'CP-UA-AD': 0,     // Amber Dusk
        'CP-UA-BL': 0,    // Birchwood & Lavender
        'CP-UA-VP': 100,     // Vanilla Peach
        'CP-UA-TB': 0,     // Tropical Berry
        'CP-UA-CI': 0,     // Citrus & Salt
        'CP-UA-AG': 0,     // Amber Glow
        'CP-UA-SC': 0,     // Soft Cashmere
    };

    // ── SKU catalog (variant metadata + campaign gates) ────────
    var WEB_SKUS = {
        'CP-UA-FP': {
            variantTitle: 'Fresh Powder',
            campaignGate: { isActive: true, sku: 'CP-UA-FP', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Fresh_Powder_Exploding_Ingredients_UA_Stick_Transparent_Background_100_Hr_Cap_100Hr_3Feb2026.png?v=1770147996&width=700'
        },
        'CP-UA-LC': {
            variantTitle: 'Lavender Citrus',
            campaignGate: { isActive: true, sku: 'CP-UA-LC', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Lavender_Citrus_Exploding_Ingredients_UA_Stick_Transparent_Background_100_Hr_Cap_100Hr_3Feb2026.png?v=1770148013&width=700'
        },
        'CP-UA-SW': {
            variantTitle: 'Sandalwood',
            campaignGate: { isActive: true, sku: 'CP-UA-SW', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Sandalwood_Exploding_Ingredients_UA_Stick_Transparent_Background_100_Hr_Cap_100Hr_3Feb2026.png?v=1770148031&width=700'
        },
        'CP-UA-MB': {
            variantTitle: 'Mountain Breeze',
            campaignGate: { isActive: true, sku: 'CP-UA-MB', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Mountain_Breeze_Exploding_Ingredients_UA_Stick_Transparent_Background_100_Hr_Cap_100Hr_3Feb2026.png?v=1770148048&width=700'
        },
        'CP-UA-CV': {
            variantTitle: 'Coconut Vanilla',
            campaignGate: { isActive: true, sku: 'CP-UA-CV', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Coconut_Vanilla_Exploding_Ingredients_UA_Stick_Transparent_Background_100_Hr_Cap_100Hr_3Feb2026.png?v=1770148065&width=700'
        },
        'CP-UA-CL': {
            variantTitle: 'Clean Sport',
            campaignGate: { isActive: true, sku: 'CP-UA-CL', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Clean_Sport_Exploding_Ingredients_UA_Stick_Transparent_Background_100_Hr_Cap_100Hr_3Feb2026.png?v=1770148085&width=700'
        },
        'CP-UA-BO': {
            variantTitle: 'Bergamot & Oud',
            campaignGate: { isActive: true, sku: 'CP-UA-BO', supportedAdsets: ['Kasper_WMVolleyBallGirls'] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Bergamot___Oud_Exploding_Orange_Stick_Ingredients_20Jan2026.png?v=1769004847&width=700'
        },
        'CP-UA-SP': {
            variantTitle: 'Cucumber Spa',
            campaignGate: { isActive: true, sku: 'CP-UA-SP', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Cucumber_Spa_Exploding_Ingredients_UA_Stick_Transparent_Background_100_Hr_Cap_100Hr_3Feb2026.png?v=1770148082&width=700'
        },
        'CP-UA-FF': {
            variantTitle: 'Fragrance Free',
            campaignGate: { isActive: true, sku: 'CP-UA-FF', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Fragrance_Free_Stick_with_Scent_Elements_Transparent_23Jul2026.png?v=1784831667&width=700'
        },
        'CP-UA-RW': {
            variantTitle: 'Rose Water',
            campaignGate: { isActive: true, sku: 'CP-UA-RW', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Rose_Water_Exploding_Ingredients_UA_Stick_Transparent_Background_100_Hr_Cap_100Hr_3Feb2026.png?v=1770148116&width=700'
        },
        'CP-UA': {
            variantTitle: 'Eucalyptus',
            campaignGate: { isActive: true, sku: 'CP-UA-EU', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/Eucalyptus_Exploding_Ingredients_UA_Stick_Transparent_Background_100_Hr_Cap_100Hr_3Feb2026.png?v=1770148133&width=700'
        },
        'CP-UA-OS': {
            variantTitle: 'Ocean Storm',
            campaignGate: { isActive: true, sku: 'CP-UA-OS', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/OS_Exploding_Ingredients_UA_Stick_Transparent_Background_100Hr_Working_File_3Feb2026.png?v=1772034813&width=700'
        },
        'CP-UA-BP': {
            variantTitle: 'Bourbon Pepper',
            campaignGate: { isActive: true, sku: 'CP-UA-BP', supportedAdsets: ['UA-Bourbon-Pepper'] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/BP_Exploding_Ingredients_UA_Stick_Transparent_Background_100Hr_Working_File_3Feb2026.png?v=1772034813&width=700'
        },
        'CP-UA-AD': {
            variantTitle: 'Amber Dusk',
            campaignGate: { isActive: true, sku: 'CP-UA-AD', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/AD_Exploding_Ingredients_UA_Stick_Transparent_Background_100Hr_Working_File_3Feb2026.png?v=1772034813&width=700'
        },
        'CP-UA-BL': {
            variantTitle: 'Birchwood & Lavender',
            campaignGate: { isActive: true, sku: 'CP-UA-BL', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/BL_Exploding_Ingredients_UA_Stick_Transparent_Background_100Hr_Working_File_3Feb2026.png?v=1772034813&width=700'
        },
        'CP-UA-VP': {
            variantTitle: 'Vanilla Peach',
            campaignGate: { isActive: true, sku: 'CP-UA-VP', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/VP_Exploding_Ingredients_UA_Stick_Transparent_Background_100Hr_Working_File_3Feb2026.png?v=1772034813&width=700'
        },
        'CP-UA-TB': {
            variantTitle: 'Tropical Berry',
            campaignGate: { isActive: true, sku: 'CP-UA-TB', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/TB_Exploding_Ingredients_UA_Stick_Transparent_Background_100Hr_Working_File_3Feb2026.png?v=1772034813&width=700'
        },
        'CP-UA-CI': {
            variantTitle: 'Citrus & Salt',
            campaignGate: { isActive: false, sku: 'CP-UA-CI', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/CI_Exploding_Ingredients_UA_Stick_Transparent_Background_100Hr_Working_File_3Feb2026.png?v=1772034813&width=700'
        },
        'CP-UA-AG': {
            variantTitle: 'Amber Glow',
            campaignGate: { isActive: true, sku: 'CP-UA-AG', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/AG_Exploding_Ingredients_UA_Stick_Transparent_Background_100Hr_Working_File_3Feb2026.png?v=1772034813&width=700'
        },
        'CP-UA-SC': {
            variantTitle: 'Soft Cashmere',
            campaignGate: { isActive: true, sku: 'CP-UA-SC', supportedAdsets: [] },
            pdpImg: 'https://cdn.shopify.com/s/files/1/0335/1622/4645/files/AG_Exploding_Ingredients_UA_Stick_Transparent_Background_100Hr_Working_File_3Feb2026.png?v=1772034813&width=700'
        },
    }

    // ===================== config / constants =====================

    var DEFAULT_OPTS = {
        desiredVisible: DESIRED_VISIBLE,
        storageKey: STORAGE_KEY,
        ttlMs: 7 * 24 * 60 * 60 * 1000,
        containerSelector: "[data-inventory-mgmt-container]",
        bundleBuilder: {
            containerId: "product-4996364566661",
            dropdownClass: "bundle-builder-item-scent-dropdown"
        },
    };
    var URL_PARAMS_KEY = "urlParams";
    var DEBUG_KEY = "carpe_inv_debug";

    // ===================== debug =====================

    // Activate: localStorage.setItem('carpe_inv_debug', '1')
    // Deactivate: localStorage.removeItem('carpe_inv_debug')
    function isDebug() {
        try { return !!localStorage.getItem(DEBUG_KEY); } catch (e) { return false; }
    }

    function dbg(label, data) {
        if (isDebug()) console.log("%c[inv:" + label + "]", "color:#e87b35;font-weight:bold", data);
    }

    // ===================== pure helpers =====================

    function clampPercent(p) {
        var n = typeof p === "number" ? p : 0;
        return n < 0 ? 0 : n > 100 ? 100 : n;
    }

    // Looks up the proportional weight for a SKU (0–100).
    function getWeight(sku) {
        return clampPercent(SKU_WEIGHTS[sku]);
    }

    function unique(arr) {
        var seen = {};
        return arr.filter(function (v) {
            if (seen[v]) return false;
            seen[v] = true;
            return true;
        });
    }

    function shuffle(arr) {
        var a = arr.slice();
        for (var i = a.length - 1; i > 0; i--) {
            var j = Math.floor(Math.random() * (i + 1));
            var tmp = a[i];
            a[i] = a[j];
            a[j] = tmp;
        }
        return a;
    }

    function weightedSampleWithoutReplacement(items, k, weightFn) {
        var scored = [];
        for (var i = 0; i < items.length; i++) {
            var w = weightFn(items[i]);
            if (w <= 0) continue;
            var u = Math.random();
            if (u === 0) u = Number.MIN_VALUE;
            scored.push({ item: items[i], key: -Math.log(u) / w });
        }
        scored.sort(function (a, b) {
            return a.key - b.key;
        });
        return scored.slice(0, k).map(function (s) {
            return s.item;
        });
    }

    // ===================== campaign gate =====================

    // Reads utm_content from sessionStorage. Returns lowercase string or empty.
    function getStoredUtmContent() {
        try {
            var stored = sessionStorage.getItem(URL_PARAMS_KEY);
            if (!stored) { dbg("utm", "no urlParams in sessionStorage"); return ""; }
            var obj = JSON.parse(stored);
            if (!obj || typeof obj !== "object") return "";
            var val = obj.utm_content;
            var result = (typeof val === "string" && val.trim()) ? val.toLowerCase() : "";
            dbg("utm", result || "(empty)");
            return result;
        } catch (e) {
            return "";
        }
    }

    // Step 1: SKUs whose campaignGate.sku appears in utm_content — always show.
    function getSkuGatedMatches(WEB_SKUS, utmNorm) {
        if (!utmNorm) return [];
        var matches = [];
        for (var sku in WEB_SKUS) {
            if (!Object.prototype.hasOwnProperty.call(WEB_SKUS, sku)) continue;
            var cfg = WEB_SKUS[sku];
            if (!cfg || !cfg.campaignGate || !cfg.campaignGate.isActive) continue;
            var gateSku = cfg.campaignGate.sku;
            if (gateSku && utmNorm.indexOf(String(gateSku).toLowerCase()) !== -1) {
                matches.push(sku);
            }
        }
        dbg("gate:sku", matches);
        return matches;
    }

    // Step 2: SKUs matched via supportedAdsets (excluding already sku-gated) — always show.
    function getAdsetGatedMatches(WEB_SKUS, utmNorm, alreadyGated) {
        if (!utmNorm) return [];
        var skip = new Set(alreadyGated);
        var matches = [];
        for (var sku in WEB_SKUS) {
            if (!Object.prototype.hasOwnProperty.call(WEB_SKUS, sku)) continue;
            if (skip.has(sku)) continue;
            var cfg = WEB_SKUS[sku];
            if (!cfg || !cfg.campaignGate || !cfg.campaignGate.isActive) continue;
            var adsets = cfg.campaignGate.supportedAdsets;
            if (!Array.isArray(adsets) || adsets.length === 0) continue;
            var hit = adsets.some(function (ad) {
                return typeof ad === "string" && ad.length > 0 && utmNorm.indexOf(ad.toLowerCase()) !== -1;
            });
            if (hit) matches.push(sku);
        }
        dbg("gate:adset", matches);
        return matches;
    }

    // Returns { skuGated, adsetGated, all } — combined campaign gate results.
    function getAllGatedSkus(WEB_SKUS) {
        var utmNorm = getStoredUtmContent();
        var skuGated = getSkuGatedMatches(WEB_SKUS, utmNorm);
        var adsetGated = getAdsetGatedMatches(WEB_SKUS, utmNorm, skuGated);
        dbg("gate:all", skuGated.concat(adsetGated));
        return { skuGated: skuGated, adsetGated: adsetGated, all: skuGated.concat(adsetGated) };
    }

    function gateSignatureFromGated(gatedSkus) {
        var copy = gatedSkus.slice();
        copy.sort();
        return copy.join("|");
    }

    // ===================== persistence =====================

    function readPersisted(key) {
        try {
            var raw = localStorage.getItem(key);
            if (!raw) return null;
            return JSON.parse(raw);
        } catch (e) {
            return null;
        }
    }

    function writePersisted(key, obj) {
        try {
            localStorage.setItem(key, JSON.stringify(obj));
        } catch (e) {
            // ignore
        }
    }

    function isPersistedValid(persisted, ttlMs, gateSignature, presentSkus, desiredVisible, WEB_SKUS, gatedNow) {
        if (!persisted || !persisted.skus || !persisted.ts) { dbg("cache", "miss: no data"); return false; }

        if (ttlMs > 0 && Date.now() - persisted.ts > ttlMs) { dbg("cache", "miss: expired"); return false; }
        if (persisted.gateSignature !== gateSignature) {
            dbg("cache", { reason: "miss: gate signature changed", was: persisted.gateSignature, now: gateSignature });
            return false;
        }
        if (persisted.skus.length < desiredVisible) { dbg("cache", "miss: fewer skus than desiredVisible"); return false; }

        var presentSet = new Set(presentSkus);
        for (var i = 0; i < desiredVisible; i++) {
            var sku = persisted.skus[i];
            if (!presentSet.has(sku)) { dbg("cache", "miss: " + sku + " no longer in DOM"); return false; }
            var cfg = WEB_SKUS[sku];
            if (!isSkuEligible(cfg, sku, gatedNow)) { dbg("cache", "miss: " + sku + " no longer eligible"); return false; }
        }

        dbg("cache", { hit: true, skus: persisted.skus.slice(0, desiredVisible) });
        return true;
    }

    // ===================== eligibility =====================

    // A SKU is eligible if it has weight > 0 OR is campaign-gated.
    function isSkuEligible(cfg, sku, allGatedSkus) {
        if (!cfg) return false;
        if (getWeight(sku) > 0) return true;
        return allGatedSkus.indexOf(sku) !== -1;
    }

    // ===================== selection logic =====================

    // Step 3: Flip weighted coins for non-gated SKUs.
    // 100% visiblePercent → automatic winner. Others → coin flip with their weight.
    function flipWeightedCoins(candidates) {
        var winners = [];
        var losers = [];
        candidates.forEach(function (sku) {
            var pct = getWeight(sku);
            if (pct >= 100) {
                winners.push(sku);
            } else if (pct > 0) {
                (Math.random() < pct / 100 ? winners : losers).push(sku);
            }
        });
        return { winners: winners, losers: losers };
    }

    // Step 4: Build final visible list.
    // Priority: gated SKUs (always) → coin-flip winners → weighted backfill from losers.
    function chooseSkus(WEB_SKUS, presentSkus, skuToNode, desiredVisible, gateResult) {
        // Gated SKUs that exist in the DOM — these always appear
        var mustShow = unique(gateResult.all.filter(function (sku) {
            return !!skuToNode[sku];
        }));
        dbg("choose:mustShow", mustShow);

        // Non-gated candidates with weight > 0
        var mustShowSet = new Set(mustShow);
        var candidates = presentSkus.filter(function (sku) {
            if (mustShowSet.has(sku)) return false;
            return getWeight(sku) > 0;
        });
        dbg("choose:candidates", candidates.map(function (s) { return s + "(" + getWeight(s) + "%)"; }));

        var remainingSlots = Math.max(0, desiredVisible - mustShow.length);
        var coinFlip = flipWeightedCoins(candidates);
        dbg("choose:coinFlip", { winners: coinFlip.winners, losers: coinFlip.losers, remainingSlots: remainingSlots });

        // Fill remaining slots: winners first
        var fillers = shuffle(coinFlip.winners).slice(0, remainingSlots);

        // Backfill from losers if needed (weighted, then random as last resort)
        if (fillers.length < remainingSlots) {
            var need = remainingSlots - fillers.length;
            var backfill = weightedSampleWithoutReplacement(coinFlip.losers, need, function (sku) {
                return getWeight(sku);
            });
            fillers = fillers.concat(backfill);
            dbg("choose:backfill", { need: need, backfilled: backfill });

            if (fillers.length < remainingSlots) {
                var used = new Set(fillers);
                var leftovers = coinFlip.losers.filter(function (s) { return !used.has(s); });
                fillers = fillers.concat(shuffle(leftovers).slice(0, remainingSlots - fillers.length));
            }
        }

        // Gated SKUs pinned to the front (shuffled among themselves) so
        // finalVisible[0] is always gated when the campaign gate fires —
        // PDP featured image and PLP reorder both read from this order.
        var result = shuffle(mustShow).concat(shuffle(fillers));
        dbg("choose:final", result);
        return result;
    }

    // Computes visible SKU list (from cache or fresh selection) and persists.
    function computeFinalVisible(WEB_SKUS, presentSkus, skuToNode, opts) {
        opts = opts || {};
        var baseDesired = typeof opts.desiredVisible === "number" ? opts.desiredVisible : DEFAULT_OPTS.desiredVisible;
        var storageKey = opts.storageKey || DEFAULT_OPTS.storageKey;
        var ttlMs = typeof opts.ttlMs === "number" ? opts.ttlMs : DEFAULT_OPTS.ttlMs;

        var gateResult = getAllGatedSkus(WEB_SKUS);
        var allGated = gateResult.all;

        // Expand slot count so all gated SKUs always fit
        var presentGatedCount = allGated.filter(function (sku) { return !!skuToNode[sku]; }).length;
        var desiredVisible = Math.max(baseDesired, presentGatedCount);

        // Cap at total eligible
        var eligibleCount = presentSkus.filter(function (sku) {
            return isSkuEligible(WEB_SKUS[sku], sku, allGated);
        }).length;
        desiredVisible = Math.min(desiredVisible, eligibleCount);

        dbg("compute", { baseDesired: baseDesired, presentGatedCount: presentGatedCount, eligibleCount: eligibleCount, desiredVisible: desiredVisible, presentSkus: presentSkus });

        var gateSignature = gateSignatureFromGated(allGated);
        var persisted = readPersisted(storageKey);

        var finalVisible;
        if (persisted && isPersistedValid(persisted, ttlMs, gateSignature, presentSkus, desiredVisible, WEB_SKUS, allGated)) {
            finalVisible = persisted.skus.slice(0, desiredVisible);
        } else {
            finalVisible = chooseSkus(WEB_SKUS, presentSkus, skuToNode, desiredVisible, gateResult);
            writePersisted(storageKey, { skus: finalVisible, ts: Date.now(), gateSignature: gateSignature });
        }

        return finalVisible;
    }

    // ===================== apply (DOM) =====================

    function normalizeTitle(str) {
        return (typeof str === "string" ? str : "").trim().toLowerCase();
    }

    function uniqueElements(arr) {
        var seen = new Set();
        var out = [];
        for (var i = 0; i < arr.length; i++) {
            var el = arr[i];
            if (!el || seen.has(el)) continue;
            seen.add(el);
            out.push(el);
        }
        return out;
    }

    function createNodeSelectionStrategyTitleFromWebSkus() {
        return {
            name: "title",
            nodeSelector: "[data-inventory-mgmt-item]",
            getKeyFromNode: function (node) { return normalizeTitle(node && node.getAttribute("data-variant-title")); },
            getKeyFromSku: function (sku, cfg) { return normalizeTitle(cfg && cfg.variantTitle); }
        };
    }

    function createNodeSelectionStrategySkuFromDom() {
        return {
            name: "sku",
            nodeSelector: "[data-inventory-mgmt-item]",
            getKeyFromNode: function (node) { return (node && node.getAttribute("data-variant-sku")) || ""; },
            getKeyFromSku: function (sku) { return sku || ""; }
        };
    }

    function shouldApplySkuVisibility(mapping, container, strategy) {
        if (!mapping || !container || !strategy) return false;
        var candidateCount = container.querySelectorAll(strategy.nodeSelector).length;
        var matchedNodes = uniqueElements(Object.values(mapping.skuToNode || {}));
        var matchedCount = matchedNodes.length;
        if (candidateCount === 0) {
            dbg("dom:no-items", { strategy: strategy.name });
            return false;
        }
        var ok = matchedCount > 0 && matchedCount === candidateCount;
        if (!ok) {
            dbg("dom:guard:bail", { candidateCount: candidateCount, matchedCount: matchedCount, strategy: strategy.name });
        }
        return ok;
    }

    function isReorderEnabled(container) {
        return !!container && container.getAttribute("data-inventory-mgmt-reorder") === "true";
    }

    // ===================== loading state (opt-in) =====================

    var INVENTORY_MGMT_HIDE_WHILE_LOADING_ATTR = "data-inventory-mgmt-hide-while-loading";
    var INVENTORY_MGMT_READY_ATTR = "data-inventory-mgmt-ready";
    var INVENTORY_MGMT_LOADER_SELECTOR = "[data-inventory-mgmt-loader]";

    function isHideWhileLoadingEnabled(container) {
        return !!container && container.getAttribute(INVENTORY_MGMT_HIDE_WHILE_LOADING_ATTR) === "true";
    }

    function getInventoryMgmtLoader(container) {
        return container ? container.querySelector(INVENTORY_MGMT_LOADER_SELECTOR) : null;
    }

    function setInventoryMgmtReady(container, isReady) {
        if (!container) return;
        container.setAttribute(INVENTORY_MGMT_READY_ATTR, isReady ? "true" : "false");
        container.setAttribute("aria-busy", isReady ? "false" : "true");

        var loader = getInventoryMgmtLoader(container);
        if (loader) {
            loader.setAttribute("aria-busy", isReady ? "false" : "true");
        }
    }

    // Builds { presentSkus, skuToNode, nodeToSku, allNodes } for the given strategy.
    // Keeps SKU as the internal ID; the strategy only controls how we locate nodes for those SKUs.
    function buildSkuNodeMapping(container, WEB_SKUS, strategy, fallbackStrategy) {
        var allNodes = uniqueElements(
            Array.prototype.slice.call(container.querySelectorAll("[data-inventory-mgmt-item]"))
        );

        var primaryNodes = Array.prototype.slice.call(container.querySelectorAll(strategy.nodeSelector));
        var keyToNode = Object.create(null);
        var duplicateKeys = [];

        for (var i = 0; i < primaryNodes.length; i++) {
            var node = primaryNodes[i];
            var key = strategy.getKeyFromNode(node);
            if (!key) continue;
            if (keyToNode[key] && keyToNode[key] !== node) duplicateKeys.push(key);
            if (!keyToNode[key]) keyToNode[key] = node;
        }

        if (duplicateKeys.length) {
            dbg("dom:dupKeys:" + strategy.name, unique(duplicateKeys));
        }

        var skuToNode = Object.create(null);
        var presentSkus = [];
        var nodeToSku = new WeakMap();
        var missing = [];

        for (var sku in WEB_SKUS) {
            if (!Object.prototype.hasOwnProperty.call(WEB_SKUS, sku)) continue;
            var cfg = WEB_SKUS[sku];
            var skuKey = strategy.getKeyFromSku(sku, cfg);
            var hit = skuKey ? keyToNode[skuKey] : null;

            if (!hit && fallbackStrategy) {
                var fallbackKey = fallbackStrategy.getKeyFromSku(sku, cfg);
                if (fallbackKey) {
                    var fallbackNode = container.querySelector(
                        fallbackStrategy.nodeSelector + '[data-variant-sku="' + fallbackKey.replace(/"/g, '\\"') + '"]'
                    );
                    hit = fallbackNode || null;
                }
            }

            if (!hit) {
                missing.push(sku);
                continue;
            }

            skuToNode[sku] = hit;
            presentSkus.push(sku);
            nodeToSku.set(hit, sku);
        }

        if (missing.length) {
            dbg("dom:missing:" + strategy.name, missing);
        }

        return { presentSkus: presentSkus, skuToNode: skuToNode, nodeToSku: nodeToSku, allNodes: allNodes };
    }

    // Picks N visible SKUs (campaign gate + weighted), persists, then shows only those in the grid and reorders.
    function applySkuVisibility(options) {
        var opts = Object.assign({}, DEFAULT_OPTS, options || {});
        var WEB_SKUS = opts.webSkus || window.WEB_SKUS;

        var desiredVisible = typeof opts.desiredVisible === "number" ? opts.desiredVisible : DEFAULT_OPTS.desiredVisible;
        var containerSelector = opts.containerSelector || DEFAULT_OPTS.containerSelector;
        var storageKey = opts.storageKey || DEFAULT_OPTS.storageKey;
        var ttlMs = typeof opts.ttlMs === "number" ? opts.ttlMs : DEFAULT_OPTS.ttlMs;

        var container = document.querySelector(containerSelector);
        if (!container || !WEB_SKUS) {
            return [];
        }

        var hideWhileLoadingEnabled = isHideWhileLoadingEnabled(container);

        container.dataset.applySkuVisibilityCalls = String(
            (Number(container.dataset.applySkuVisibilityCalls || "0") + 1)
        );

        var titleStrategy = createNodeSelectionStrategyTitleFromWebSkus();
        var skuFallbackStrategy = createNodeSelectionStrategySkuFromDom();
        var mapping = buildSkuNodeMapping(container, WEB_SKUS, titleStrategy, skuFallbackStrategy);
        var presentSkus = mapping.presentSkus;
        var skuToNode = mapping.skuToNode;
        var nodeToSku = mapping.nodeToSku;
        var allNodes = mapping.allNodes;

        var finalVisible = [];
        try {
            if (presentSkus.length === 0) return [];
            if (!shouldApplySkuVisibility(mapping, container, titleStrategy)) return [];

            if (hideWhileLoadingEnabled) {
                setInventoryMgmtReady(container, false);
            }

            finalVisible = computeFinalVisible(WEB_SKUS, presentSkus, skuToNode, {
                desiredVisible: desiredVisible,
                storageKey: storageKey,
                ttlMs: ttlMs
            });

            var visibleSet = new Set(finalVisible);
            uniqueElements(Object.values(skuToNode)).forEach(function (node) {
                var sku = nodeToSku.get(node) || "";
                if (!sku) return;
                var isVisible = visibleSet.has(sku);
                node.style.display = isVisible ? "" : "none";
                node.setAttribute("aria-hidden", isVisible ? "false" : "true");
            });

            // Tell other parts of the page (e.g. the PDP ingredients list) which scents this
            // container manages and which of them are shown. Kept on window for late readers.
            var titleOf = function (sku) { return (WEB_SKUS[sku] && WEB_SKUS[sku].variantTitle) || ""; };
            window.carpeInventoryVisibility = {
                managedTitles: presentSkus.map(titleOf),
                visibleTitles: finalVisible.map(titleOf)
            };
            document.dispatchEvent(new CustomEvent("inventory-mgmt:applied", { detail: window.carpeInventoryVisibility }));

            if (isReorderEnabled(container)) {
                var firstNode = null;
                for (var i = 0; i < finalVisible.length; i++) {
                    var sku = finalVisible[i];
                    if (skuToNode[sku]) { firstNode = skuToNode[sku]; break; }
                }
                var reorderParent = firstNode && firstNode.parentElement;
                if (!reorderParent) {
                    dbg("dom:reorder:skip", { reason: "no parentElement", containerSelector: containerSelector });
                } else {
                    finalVisible.forEach(function (sku) {
                        if (skuToNode[sku]) reorderParent.appendChild(skuToNode[sku]);
                    });
                    // Pin nodes opt-in to always render after the reordered items
                    var pinnedLast = reorderParent.querySelectorAll("[data-inventory-mgmt-pin-last]");
                    for (var p = 0; p < pinnedLast.length; p++) {
                        reorderParent.appendChild(pinnedLast[p]);
                    }
                }
            }
        } finally {
            if (hideWhileLoadingEnabled) {
                setInventoryMgmtReady(container, true);
            }
        }

        return finalVisible;
    }

    // ===================== bundle builder: filter dropdown options =====================

    var BUNDLE_BUILDER_SKU_PATTERN = /^CP-UA[\w-]*$/;

    function firstVisibleInOptions(visibleOrder, optionElements) {
        for (var i = 0; i < visibleOrder.length; i++) {
            var sku = visibleOrder[i];
            for (var j = 0; j < optionElements.length; j++) {
                if (optionElements[j].value === sku) return sku;
            }
        }
        return null;
    }

    // Filters each scent dropdown to the same N visible SKUs and resets value if current selection is no longer visible.
    function applyBundleBuilderDropdowns(options) {
        var opts = Object.assign({}, DEFAULT_OPTS, options || {});
        var WEB_SKUS = opts.webSkus || window.WEB_SKUS;
        if (!WEB_SKUS) return;

        var containerId = opts.containerId || DEFAULT_OPTS.bundleBuilder.containerId;
        var dropdownClass = opts.dropdownClass || DEFAULT_OPTS.bundleBuilder.dropdownClass;
        var desiredVisible = typeof opts.desiredVisible === "number" ? opts.desiredVisible : DEFAULT_OPTS.desiredVisible;
        var storageKey = opts.storageKey || DEFAULT_OPTS.storageKey;
        var ttlMs = typeof opts.ttlMs === "number" ? opts.ttlMs : DEFAULT_OPTS.ttlMs;

        var container = document.getElementById(containerId);
        if (!container) return;

        var dropdownSelector = dropdownClass.indexOf(".") === 0 ? dropdownClass : "." + dropdownClass;
        var dropdowns = container.querySelectorAll(dropdownSelector);
        if (!dropdowns.length) return;

        var firstSelect = dropdowns[0];
        var presentSkus = [];
        var optionsList = Array.prototype.slice.call(firstSelect.options);

        for (var i = 0; i < optionsList.length; i++) {
            var val = optionsList[i].value;
            if (typeof val !== "string" || val.trim() === "") continue;
            if (!BUNDLE_BUILDER_SKU_PATTERN.test(val) || !WEB_SKUS[val]) {
                console.warn("[proportional-inventory-mgmt] Bundle builder option value is not a known SKU:", val);
                return;
            }
            if (presentSkus.indexOf(val) === -1) presentSkus.push(val);
        }

        if (presentSkus.length === 0) return;

        var skuToNode = Object.create(null);
        presentSkus.forEach(function (sku) {
            skuToNode[sku] = true;
        });

        var finalVisible = computeFinalVisible(WEB_SKUS, presentSkus, skuToNode, {
            desiredVisible: desiredVisible,
            storageKey: storageKey,
            ttlMs: ttlMs
        });

        var finalSet = Object.create(null);
        finalVisible.forEach(function (sku) {
            finalSet[sku] = true;
        });

        for (var d = 0; d < dropdowns.length; d++) {
            var select = dropdowns[d];
            var selectOptions = Array.prototype.slice.call(select.options);
            var currentValue = select.value;

            for (var j = selectOptions.length - 1; j >= 0; j--) {
                if (!finalSet[selectOptions[j].value]) {
                    selectOptions[j].remove();
                }
            }

            var remainingOptions = Array.prototype.slice.call(select.options);
            var currentStillPresent = false;
            for (var k = 0; k < remainingOptions.length; k++) {
                if (remainingOptions[k].value === currentValue) {
                    currentStillPresent = true;
                    break;
                }
            }
            if (!currentStillPresent && remainingOptions.length > 0) {
                var firstVisibleSku = firstVisibleInOptions(finalVisible, remainingOptions);
                select.value = firstVisibleSku || remainingOptions[0].value;
                select.dispatchEvent(new Event("change", { bubbles: true }));
            }
        }
    }

    // Sets PDP featured image to the first visible SKU's pdpImg.
    function applyPdpFeaturedImage(options) {
        var opts = Object.assign({}, DEFAULT_OPTS, options || {});
        var WEB_SKUS = opts.webSkus || window.WEB_SKUS;
        if (!WEB_SKUS) return;

        var container = document.querySelector("[data-proportional-inventory-pdp-featured]");
        if (!container) return;

        var img = container.querySelector("[data-proportional-inventory-featured-img]");
        if (!img) return;

        var storageKey = opts.storageKey || DEFAULT_OPTS.storageKey;
        var ttlMs = typeof opts.ttlMs === "number" ? opts.ttlMs : DEFAULT_OPTS.ttlMs;
        var presentSkus = Object.keys(WEB_SKUS);
        var skuToNode = Object.create(null);
        presentSkus.forEach(function (sku) {
            skuToNode[sku] = true;
        });

        var finalVisible = computeFinalVisible(WEB_SKUS, presentSkus, skuToNode, {
            desiredVisible: opts.desiredVisible,
            storageKey: storageKey,
            ttlMs: ttlMs
        });
        var firstSku = finalVisible[0];
        var cfg = firstSku && WEB_SKUS[firstSku];
        if (cfg && cfg.pdpImg) {
            img.src = cfg.pdpImg;
        }
    }

    // ===================== init =====================

    document.addEventListener("DOMContentLoaded", function () {
        var storageKey = DEFAULT_OPTS.storageKey;
        var ttlMs = DEFAULT_OPTS.ttlMs;
        var desiredVisible = DEFAULT_OPTS.desiredVisible;
        applySkuVisibility({ webSkus: WEB_SKUS, desiredVisible: desiredVisible, storageKey: storageKey, ttlMs: ttlMs });

        applyBundleBuilderDropdowns({
            webSkus: WEB_SKUS,
            desiredVisible: desiredVisible,
            containerId: DEFAULT_OPTS.bundleBuilder.containerId,
            storageKey: storageKey,
            ttlMs: ttlMs
        });

        applyPdpFeaturedImage({ webSkus: WEB_SKUS, desiredVisible: desiredVisible, storageKey: storageKey, ttlMs: ttlMs });
    });

    // Re-apply SKU visibility after the DOM has changed underneath us.
    //
    // applySkuVisibility resolves its container with document.querySelector, i.e. the FIRST
    // [data-inventory-mgmt-container] in the document, and the init above only ever runs on
    // DOMContentLoaded. A page that swaps in a different container after that point — the
    // scent builder A/B replaces its grid when Convert resolves the arm — leaves the new
    // grid with no visibility applied, showing SKUs that should be hidden.
    //
    // Safe to call repeatedly: computeFinalVisible reuses the persisted selection while it
    // is valid, so a re-run applies the same SKUs it chose the first time.
    window.refreshInventoryMgmt = function () {
        return applySkuVisibility({
            webSkus: WEB_SKUS,
            desiredVisible: DEFAULT_OPTS.desiredVisible,
            storageKey: DEFAULT_OPTS.storageKey,
            ttlMs: DEFAULT_OPTS.ttlMs
        });
    };

    window.WEB_SKUS = WEB_SKUS;
})();
